import {Location} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {CookieService} from 'ngx-cookie-service';
import {Observable, Observer} from 'rxjs';
import {environment} from 'src/environments/environment';
import {Md5} from 'ts-md5';
import {Activation, ActivationRequest} from '../interfaces/activation.interface';
import {AuthData} from '../interfaces/auth-data.interface';
import {ClaimRegistration} from '../interfaces/claim-registration';
import {Interaction} from '../interfaces/interaction.interface';
import {Invitation} from '../interfaces/invitation.interface';
import {Permission} from '../interfaces/permission.interface';
import {Scope} from '../interfaces/scope.interface';
import {CredentialsI, User, UserI} from '../interfaces/user.interface';
import {Registration} from '../interfaces/userRegistration.interface';
import {ApiService} from './api.service';
import {DialogService} from './dialog.service';
import {RouterService} from './router.service';
import {StorageService} from './storage.service';
import {SearchResults, UserSearch} from "../interfaces/pager";
import {Claim2CustomerI, ClaimI} from '../interfaces/claim.interface';
import {CustomerDomainI} from '../interfaces/customer-domain.interface';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  public context = 'sso';
  public state = {
    error: null,
    maintenance: null
  };
  public user: UserI;
  public domainAdmins: Permission[] = [];
  public authed = false;
  public authChecked = false;
  public expiresAt: Date;
  private authCBs: ((auth: boolean) => void)[] = [];
  public authObservable: Observable<boolean>;
  public authObserver: Observer<boolean>;
  private accessToken: string;
  public scopes: Scope[];
  public claims: ClaimI[];
  public customerDomainsLoaded = false;
  public allCustomerDomainsLoaded = false;
  public availableCustomerDomains: CustomerDomainI[];
  public allCustomerDomains: CustomerDomainI[];

  public invitationMd5;
  public usedHash = '';
  public oidc: {
    url?: string;
  } = {};


  constructor(
    public storage: StorageService,
    public api: ApiService,
    public cookie: CookieService,
    public router: RouterService,
    public dialog: DialogService,
    public toast: MatSnackBar,
    public location: Location,
  ) {

    this.oidc.url = environment.api.url;

    this.authObservable = new Observable((observer) => {
      this.authObserver = observer;
    });

    this.authObservable.subscribe((auth: boolean) => {
      this.authed = auth;
      for (const cb of this.authCBs) {
        cb(auth);
      }
    });

    this.storage.get('auth-data').then((authData: AuthData) => {
      this.checkState('error').then((state) => {
        this.state.error = state;
      }).catch((e: HttpErrorResponse) => {
        console.error(e);
      });

      this.checkState('maintenance').then((state) => {
        this.state.maintenance = state;
      }).catch((e: HttpErrorResponse) => {
        console.error(e);
      });

      if (authData) {
        this.user = authData.user;
        this.expiresAt = new Date(authData.expiresAt);
        this.accessToken = authData.accessToken;
        if ((new Date()).getTime() > this.expiresAt.getTime()) {
          delete this.user;
          delete this.accessToken;
          delete this.expiresAt;
          this.storage.remove('auth-data');
          this.authObserver.next(false);
          this.router.navigateByUrl('/');
        } else {
          this.authObserver.next(true);
        }
      } else {
        this.saveInvitationCode();
        this.authObserver.next(false);
      }

      this.authChecked = true;
    });
  }

  private async checkState(type: string): Promise<string> {
    return (await this.api.get('state/' + type)).msg;
  }

  public isAuthed(cb: (auth: boolean) => void): void {
    if (this.authChecked) {
      cb(this.authed);
    }

    this.authCBs.push(cb);
  }

  private async tokenExpiry(): Promise<void> {
    const now = new Date();
    if (now.getTime() > this.expiresAt.getTime()) {
      await this.logout();
    } else {
      setTimeout(async () => {
        this.expiresAt = new Date((await this.api.post('token-expiry', {}) as any).expiresAt);
        this.tokenExpiry();
      }, (this.expiresAt.getTime() - now.getTime()));
    }
  }

  private saveInvitationCode() {
    if (this.location.path().includes('einladungscode') || this.location.path().includes('einladungs-code')) {
      this.storage.set('redirect-route', this.location.path());
    }
  }

  public async auth(credentials: CredentialsI): Promise<boolean> {
    // credentials.password = this.hash(credentials.password);
    const authData = await this.api.post('auth', credentials) as AuthData;
    this.user = authData.user;
    this.accessToken = authData.accessToken;
    this.expiresAt = new Date(authData.expiresAt);
    this.storage.set('auth-data', authData);
    this.tokenExpiry();
    this.authObserver.next(true);
    this.authChecked = true;
    return true;
  }

  public async logout(): Promise<boolean> {
    await this.api.post('logout', {});
    delete this.user;
    delete this.accessToken;
    delete this.expiresAt;
    this.storage.remove('auth-data');
    this.storage.remove('redirect-route');
    this.authObserver.next(false);
    this.router.navigateByUrl('/');
    return true;
  }

  public getToken(): string {
    return this.accessToken;
  }

  public async getInteraction(uid: string): Promise<Interaction> {
    return await this.api.get('interaction/' + uid);
  }

  public async consent(uid: string): Promise<boolean> {
    return await this.api.post('consent/' + uid);
  }

  public async lostPassword(email: string): Promise<boolean> {
    return await this.api.post('lost-password', {email});
  }

  public async newEmail(email: string): Promise<boolean> {
    return await this.api.post('new-email', {email});
  }

  public async register(user: Registration): Promise<UserI> {
    return await this.api.post('registration', user);
  }

  public async updateUser(user: User): Promise<UserI> {
    if (!user.id) {
      user.id = user.sub;
    }

    return await this.api.put('users/' + user.id, {user});
  }

  public async checkActivationKey(key: string): Promise<Activation> {
    return await this.api.get('activation/' + key);
  }

  public async activate(activationRequest: ActivationRequest): Promise<boolean> {
    return await this.api.post('activation', activationRequest);
  }

  public async updateEmail(activationRequest: ActivationRequest): Promise<string> {
    return (await this.api.post('update-email', activationRequest) as User).email;
  }

  public async updatePassword(activationRequest: ActivationRequest): Promise<boolean> {
    // activationRequest.password = this.hash(activationRequest.password);
    return await this.api.post('update-password', activationRequest);
  }

  public async getPermissions(): Promise<Permission[]> {
    const permissions = await this.api.get('permissions');
    if (permissions) {
      for (const c of permissions) {
        if (c.claim_name === 'sta_user_domain_admin') {
          this.domainAdmins.push(c);
        }
      }
      this.user.claims = permissions;
    }
    return permissions;
  }

  public async deletePermission(id: string): Promise<Permission[]> {
    return await this.api.delete('permissions/' + id);
  }

  public async confirmInvitation(invitation: Invitation): Promise<Permission[]> {
    let permissions = await this.api.post('redemption', invitation);
    let now = new Date();
    permissions = permissions.filter(p => new Date(p.expires).getTime() > now.getTime());
    if (permissions) {
      if (typeof this.user.claims === 'undefined') {
        this.user.claims = [];
      }
      for (const c of permissions) {
        if (c.claim_name === 'sta_user_domain_admin') {
          this.domainAdmins.push(c);
          break;
        }

        if (!this.user.claims.find(c2u => c2u.id === c.id)) {
          this.user.claims.push(c);
        }
      }
    }
    console.log(this.user.claims)
    return permissions;
  }

  public async getScopes(): Promise<Scope[]> {
    if (!this.scopes) {
      this.scopes = await this.api.get('scopes');
    }

    return this.scopes;
  }

  public async getClaims(): Promise<ClaimI[]> {
    if (!this.claims) {
      this.claims = await this.api.get('claims');
    }

    return this.claims;
  }

  public async getCustomerDomains(): Promise<CustomerDomainI[]> {
    if (this.customerDomainsLoaded) {
      return this.availableCustomerDomains;
    } else {
      this.availableCustomerDomains = await this.api.get('user/customer-domains');
      this.customerDomainsLoaded = true;
      return this.availableCustomerDomains;
    }
  }

  public async getAllCustomerDomains(data: string[]): Promise<CustomerDomainI[]> {
    if (this.allCustomerDomains) {
      return this.allCustomerDomains;
    } else {
      this.allCustomerDomains = await this.api.post('user/all-customer-domains', {customer_ids: data});
      this.allCustomerDomainsLoaded = true;
      return this.allCustomerDomains;
    }
  }

  public async getDomainUsers(params: UserSearch): Promise<SearchResults> {
    return await this.api.post('user/domain-users', params);
  }

  public async claim2customer(data: { claimName: string, email?: string, userId?: string }): Promise<Claim2CustomerI> {
    return await this.api.post('user/claim2customer', data);
  }

  public async deleteClaim2customer(id: string): Promise<Claim2CustomerI> {
    return await this.api.delete('user/claim2customer/' + id);
  }

  public async sendActivationMail(data: { claimName: string, c2c_id?: string }): Promise<Claim2CustomerI> {
    return await this.api.post('user/send-activation-mail', data);
  }

  public async getClaimRegistration(md5: string): Promise<ClaimRegistration> {
    return await this.api.get(`claimRegistration/${md5}`);
  }

  protected hash(str: string): string {
    return Md5.hashStr(str).toString();
  }


}
